import React, { useCallback, useState, useEffect } from "react";
import PurchaseDocDetails from "../../_components/Purchasing/PurchaseDocDetails";
import { FieldInput, SearchableSelect, SimpleSelect, DetailRow } from "../../_components/Purchasing/PurchaseShared";
import { FiCreditCard, FiList, FiDollarSign } from "react-icons/fi";
import { getPaymentVoucherById, updatePaymentVoucher, createPaymentVoucher, deletePaymentVoucher } from "../../_services/finance.service";
import { getPurchaseInvoices, getPurchaseInvoiceById } from "../../_services/purchase.service";
import { getActiveSupplierCompanyNames, getSuppliersByName } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";

// --- Constants ---
const PAYMENT_METHODS = [
    "Bank Transfer",
    "Cheque",
    "Cash",
    "Online Transfer",
    "TT",
    "Others"
];

// These would ideally come from your bank/account settings API.
// Hardcode or fetch from a future /getBankAccounts endpoint.
const PAY_FROM_OPTIONS = [
    "Maybank - 1234567890",
    "CIMB - 9876543210",
    "Public Bank - 1122334455",
];

// --- Supplier helpers (identical to PurchaseOrderDetails) ---
async function fetchContactPersons(supplierName) {
    if (!supplierName) return [];
    const data = await getSuppliersByName(supplierName);
    return data
        .filter((r) => {
            const s = (r.status || r.Status || "").toLowerCase();
            return r.name === supplierName && !!r.attention && s === "active";
        })
        .map((r) => r.attention ?? "")
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i);
}

async function fetchSupplierDetails(supplierName, contactPerson = "") {
    if (!supplierName) return {};

    const data = await getSuppliersByName(supplierName);
    const rows = data.filter((r) => r.name === supplierName);
    const first = rows[0];

    if (!first) return {};

    // Always return addresses when supplier is selected
    const base = {
        supplierAddress: first.billingAddress || first.billing_address || first.address || "",
        supplierShippingAddress: first.shippingAddress || first.shipping_address || "",
    };

    // Only return phone + payment terms when contact person is selected
    if (!contactPerson) return base;

    const match = rows.find((r) => (r.attention || "") === contactPerson) || first;

    return {
        ...base,
        paymentTerms: match.creditTerms || match.credit_terms || "",
        phoneNumber: match.phoneNumber || match.phone_number || "",
    };
}

export default function PaymentVoucherDetails() {
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);
    const [piList, setPiList] = useState([]);

    useEffect(() => {
        // Fetch all approved PIs for the linked documents selector
        getPurchaseInvoices({ pageSize: 1500, status: 'Approved' })
            .then(res => setPiList(res?.data || []))
            .catch(() => { });
    }, []);

    const extraFields = {
        // Rendered in the "Document Info" (middle) column
        left: ({ draft, onChange, errors, edit }) => {
            if (edit) {
                // Filter PIs by selected supplier
                const piOptions = piList
                    .filter(pi => pi.supplier === draft.supplier)
                    .map(pi => pi.id);

                const handlePiChange = (e) => {
                    const selectedId = e.target.value;
                    const current = draft.linkedPiIds || [];
                    // Toggle selection
                    const next = current.includes(selectedId)
                        ? current.filter(id => id !== selectedId)
                        : [...current, selectedId];
                    // Sum payAmount from linked PIs
                    const total = piList
                        .filter(pi => next.includes(pi.id))
                        .reduce((sum, pi) => sum + (parseFloat(pi.grandTotal) || 0), 0);
                    onChange({ target: { name: "linkedPiIds", value: next } });
                    onChange({ target: { name: "payAmount", value: total } });
                };

                return (
                    <>
                        {/* PI Reference No. (single select) */}
                        <SimpleSelect
                            label="PI Reference No."
                            name="piReferenceNo"
                            value={(draft.linkedPiIds && draft.linkedPiIds[0]) || ""}
                            options={[
                                { value: "", label: "Select PI reference..." },
                                ...piList
                                    .filter(pi => pi.status === "Approved" && pi.supplier === draft.supplier)
                                    .map(pi => ({
                                        value: pi.id,
                                        label: `${pi.id}${pi.refNo ? ` - ${pi.refNo}` : ""}`,
                                    })),
                            ]}
                            onChange={async (e) => {
                                const selectedId = e.target.value;

                                // Store as linkedPiIds (backend already supports this)
                                onChange({ target: { name: "linkedPiIds", value: selectedId ? [selectedId] : [] } });

                                // If cleared, clear items + totals
                                if (!selectedId) {
                                    onChange({ target: { name: "items", value: [] } });
                                    onChange({ target: { name: "payAmount", value: 0 } });
                                    return;
                                }

                                try {
                                    // Fetch PI document
                                    const pi = await getPurchaseInvoiceById(selectedId);

                                    // Map PI line items -> Payment Voucher line items
                                    // IMPORTANT: Adjust these field names if your PI items are different.
                                    const pvItems = (pi.items || []).map((it) => {
                                        const qty = Number(it.qty) || 0;
                                        const unitPrice = Number(it.unitPrice) || 0;

                                        // Prefer explicit amount if exists; fallback to qty*unitPrice
                                        const amount =
                                            Number(it.payAmount) ||
                                            Number(it.amount) ||
                                            Number(it.total) ||
                                            (qty * unitPrice) ||
                                            0;

                                        return {
                                            accountCode: "",
                                            description: it.description || "",
                                            payAmount: amount,
                                        };
                                    });

                                    const total = pvItems.reduce((sum, r) => sum + (Number(r.payAmount) || 0), 0);

                                    // Put items into the same draft.items used by PurchaseDocDetails -> LineItemsTable
                                    onChange({ target: { name: "items", value: pvItems } });

                                    // Update total payment
                                    onChange({ target: { name: "payAmount", value: total } });
                                } catch (err) {
                                    console.error("Failed to load PI details:", err);
                                    window.alert("Failed to fetch selected Purchase Invoice details.");
                                }
                            }}
                            error={errors?.linkedPiIds}
                            // Unlocked once the supplier and contact person are picked, as in Sales.
                            disabled={!draft.supplier || !draft.contactPerson}
                            required
                        />
                        <SimpleSelect
                            label="Pay From (Bank / Account)"
                            name="payFrom"
                            value={draft.payFrom || ""}
                            options={PAY_FROM_OPTIONS.map(o => ({ value: o, label: o }))}
                            onChange={onChange}
                            error={errors?.payFrom}
                            placeholder="Select bank/account..."
                            required
                        />
                        <SimpleSelect
                            label="Payment Method"
                            name="paymentMethod"
                            value={draft.paymentMethod || ""}
                            options={PAYMENT_METHODS.map(o => ({ value: o, label: o }))}
                            onChange={onChange}
                            error={errors?.paymentMethod}
                            placeholder="Select payment method..."
                            required
                        />
                    </>
                );
            }
            // View mode
            return (
                <>
                    {(draft.linkedPiIds || []).length > 0 && (
                        <DetailRow icon={FiList} label="PI Reference No.">
                            {(draft.linkedPiIds || []).join(", ")}
                        </DetailRow>
                    )}
                    {draft.payFrom && (
                        <DetailRow icon={FiCreditCard} label="Pay From">{draft.payFrom}</DetailRow>
                    )}
                    {draft.paymentMethod && (
                        <DetailRow icon={FiCreditCard} label="Payment Method">{draft.paymentMethod}</DetailRow>
                    )}
                    {draft.payAmount != null && (
                        <DetailRow icon={FiDollarSign} label="Pay Amount">
                            {`RM ${parseFloat(draft.payAmount).toFixed(2)}`}
                        </DetailRow>
                    )}
                </>
            );
        },
    };

    return (
        <PurchaseDocDetails
            docType="Payment Voucher"
            basePath="/finance/payment-vouchers"
            fetchById={getPaymentVoucherById}
            updateItem={updatePaymentVoucher}
            deleteItem={deletePaymentVoucher}
            createItem={createPaymentVoucher}
            fetchSupplierNames={getActiveSupplierCompanyNames}
            fetchContactPersons={fetchContactPersons}
            fetchSupplierDetails={fetchSupplierDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            searchable={false}
            searchSource="RM"
            extraFields={extraFields}
            emptyDraft={{
                linkedPiIds: [],
                payAmount: 0,
                payFrom: "",
                paymentMethod: "",
            }}
            // Pass Pay From and Payment Method options to extraFields via extraFieldProps
            extraFieldProps={{ piList, PAY_FROM_OPTIONS, PAYMENT_METHODS }}
            hideCurrency={false}
            lineItemProps={{
                columns: [
                    {
                        key: "accountCode",
                        label: "ACCOUNT CODE / CATEGORY",
                        width: "25%",
                        type: "select",
                        required: true,
                        options: [
                            "",
                            "Cost of Goods Sold",
                            "Operating Expenses",
                            "Administrative Expenses",
                            "Rental & Utilities",
                            "Salaries & Wages",
                            "Marketing & Advertising",
                            "Transportation & Logistics",
                            "Professional Fees",
                            "Maintenance & Repairs",
                            "Others",
                        ],
                    },
                    { key: "description", label: "DESCRIPTION", width: "40%" },
                    { key: "payAmount", label: "PAY AMOUNT (RM)", width: "20%", type: "number" },
                ],
                hideQty: true,
                hideUnit: true,
                hideUnitPrice: true,
                totalLabel: "Total Payment (RM)",
                mode: "payment_voucher",
            }}
        />
    );
}